// Trello real-time sync via WebSocket
// 
// LOCAL DEV: no WebSocket server exists, so this is a no-op.
// ON SERVER: spin up the companion server (server/wsServer.js) which:
//   - Connects to Trello's webhook API
//   - Broadcasts board changes to connected clients
//   - Clients receive events and update their local store instantly
//
// To enable: set REACT_APP_WS_URL=wss://yourserver.com/ws in .env

const WS_URL = process.env.REACT_APP_WS_URL;

class TrelloWebSocketService {
  constructor() {
    this.ws         = null;
    this.listeners  = new Set();
    this.reconnectDelay = 2000;
    this.retries    = 0;
    this.timer      = null;
    this.token      = null;
    this.enabled    = !!WS_URL;
    // A Mac that slept overnight or dropped Wi-Fi used to burn its five retries in
    // thirty seconds and never reconnect — live updates were dead until the app was
    // restarted (Ryan, Sep 30 2026). Now it keeps trying (30 s apart at most) and
    // tries straight away when the network comes back or the tab is looked at again.
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.wake());
      document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') this.wake(); });
    }
  }

  wake() {
    if (!this.enabled || !this.token || this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) return;
    clearTimeout(this.timer); this.retries = 0;
    this.connect(this.token);
  }

  connect(adminToken) {
    if (!this.enabled) return;
    this.token = adminToken;
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) return;

    try {
      this.ws = new WebSocket(`${WS_URL}?token=${adminToken}`);

      this.ws.onopen = () => {
        console.log('[WS] Connected to Titan sync server');
        this.retries = 0;
        this.notify({ type: 'connected' });
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.notify(data);
        } catch {}
      };

      this.ws.onclose = () => {
        console.log('[WS] Disconnected — reconnecting…');
        this.notify({ type: 'disconnected' });
        this.scheduleReconnect(adminToken);
      };

      this.ws.onerror = (err) => {
        console.warn('[WS] Error:', err);
      };
    } catch (e) {
      console.warn('[WS] Could not connect:', e.message);
    }
  }

  scheduleReconnect(token) {
    if (!this.token) return;   // signed out
    this.retries++;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.connect(token), Math.min(30000, this.reconnectDelay * this.retries));
  }

  disconnect() {
    this.token = null;
    clearTimeout(this.timer);
    const ws = this.ws; this.ws = null;
    if (ws) { ws.onclose = null; try { ws.close(); } catch {} }
  }

  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn); // returns unsubscribe fn
  }

  notify(data) {
    this.listeners.forEach(fn => fn(data));
  }

  isConnected() {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  isEnabled() {
    return this.enabled;
  }
}

// Singleton — one connection for the whole app
export const trelloWS = new TrelloWebSocketService();
